import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

const BUILD_CHANNEL = __BUILD_CHANNEL__;
export const BUILD_COMMIT = __GIT_COMMIT_SHORT__;

/**
 * The channels only the team runs. Any other channel, one not modelled here
 * included, is a release: no Developer menu, no build suffix.
 */
const PRE_RELEASE_CHANNELS = new Set([
  "development",
  "preview",
  "staging",
  "desktop-development",
  "desktop-staging",
]);

export const IS_PRE_RELEASE_CHANNEL = PRE_RELEASE_CHANNELS.has(BUILD_CHANNEL);

/** Whether the Developer section shows: dev builds and pre-release channels. */
export function isDevMenuVisible(): boolean {
  return import.meta.env.DEV || IS_PRE_RELEASE_CHANNEL;
}

/** Snapshot of what Capgo is currently running, for the debug panel. */
interface CapgoStatus {
  bundleId: string | null;
  bundleVersion: string | null;
  channel: string | null;
  native: string | null;
}

/**
 * Reads the current Capgo state: the native app version, the active JS bundle
 * (`builtin` until an OTA applies), and the channel the device polls.
 */
export async function getCapgoStatus(): Promise<CapgoStatus> {
  if (!Capacitor.isNativePlatform()) {
    return { bundleId: null, bundleVersion: null, channel: null, native: null };
  }
  const current = await CapacitorUpdater.current().catch(() => null);
  const channelRes = await CapacitorUpdater.getChannel().catch(() => null);
  return {
    bundleId: current?.bundle?.id ?? null,
    bundleVersion: current?.bundle?.version ?? null,
    channel: channelRes?.channel ?? null,
    native: current?.native ?? null,
  };
}

/** A human-readable Capgo lifecycle event for surfacing in the UI. */
interface CapgoEvent {
  detail?: string;
  label: string;
  level: "info" | "success" | "error";
}

/**
 * Subscribes to Capgo's update lifecycle so the app can SHOW what's happening
 * (checking, downloading %, applied, failed) instead of it being invisible.
 * Returns an unsubscribe function. No-op on web.
 */
export async function registerCapgoListeners(
  onEvent: (event: CapgoEvent) => void
): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) {
    return () => {
      // no-op unsubscribe: Capgo has no listeners on web
    };
  }

  const handles = await Promise.all([
    CapacitorUpdater.addListener("updateAvailable", (info) => {
      onEvent({
        detail: info.bundle?.version,
        label: "Update available",
        level: "info",
      });
    }),
    CapacitorUpdater.addListener("download", (info) => {
      onEvent({
        detail: `${info.percent ?? 0}%`,
        label: "Downloading update",
        level: "info",
      });
    }),
    CapacitorUpdater.addListener("downloadComplete", (info) => {
      onEvent({
        detail: info.bundle?.version,
        label: "Download complete. Restart to apply",
        level: "success",
      });
    }),
    CapacitorUpdater.addListener("noNeedUpdate", () => {
      onEvent({ label: "Already up to date", level: "info" });
    }),
    CapacitorUpdater.addListener("updateFailed", (info) => {
      onEvent({
        detail: info.bundle?.version,
        label: "Update failed (will roll back)",
        level: "error",
      });
    }),
    CapacitorUpdater.addListener("downloadFailed", (info) => {
      onEvent({
        detail: info.version,
        label: "Download failed",
        level: "error",
      });
    }),
    CapacitorUpdater.addListener("appReloaded", () => {
      onEvent({ label: "Reloaded into updated bundle", level: "success" });
    }),
  ]);

  return () => {
    for (const handle of handles) {
      handle.remove();
    }
  };
}
